/**
 * Alphabetical school directory with zone filter.
 *
 * What: Renders a browsable directory of schools as crawlable <a> links grouped by first letter.
 * Why: Enables internal linking from hub pages to school pages, supporting crawlability and SEO.
 * When: Update when school data shape changes or new zone values are added.
 */
import React, { useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { SchoolRecord } from '@shared/schools/schoolData.js';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import LandingSection from '@/components/landing/LandingSection';
import {
  colors,
  containerPadding,
} from '@/components/landing/landingTokens.js';

/**
 * Anchor id. Deliberately NOT registered with `useSectionFadeIn`: this section
 * runs to ~5,000px on a level page and past 12,000px on a hub page, so it can
 * never cover the observer's 10% threshold and would sit at `opacity: 0`
 * forever. See the height ceiling documented on `useSectionFadeIn`.
 */
const SECTION_ID = 'school-directory';

interface SchoolDirectoryProps {
  schools: readonly SchoolRecord[];
  heading?: string;
  isLoading?: boolean;
  emptyStateMessage?: string;
}

type Zone = 'ALL' | 'NORTH' | 'SOUTH' | 'EAST' | 'WEST';
const ZONES: Zone[] = ['ALL', 'NORTH', 'SOUTH', 'EAST', 'WEST'];

function zoneLabel(zone: Zone): string {
  if (zone === 'ALL') return 'All Zones';
  return zone.charAt(0) + zone.slice(1).toLowerCase();
}

function SchoolDirectory({
  schools,
  heading = 'Schools Directory',
  isLoading = false,
  emptyStateMessage = 'No schools found for this zone.',
}: SchoolDirectoryProps) {
  const [activeZone, setActiveZone] = useState<Zone>('ALL');

  const filtered =
    activeZone === 'ALL' ? schools : schools.filter(s => s.zone === activeZone);

  // Group by first letter of displayName
  const grouped: Record<string, SchoolRecord[]> = {};
  for (const school of filtered) {
    const letter = school.displayName.charAt(0).toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(school);
  }
  const letters = Object.keys(grouped).sort();

  return (
    <LandingSection
      id={SECTION_ID}
      tone="muted"
      ariaLabelledby="school-directory-heading"
      containerSx={{ px: containerPadding.px }}
    >
      <LandingCenteredSectionHead
        titleId="school-directory-heading"
        title={heading}
      />

      <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
        {/* Zone filter chips — client-side filtering, SSR renders all schools */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          justifyContent="center"
        >
          {ZONES.map(zone => {
            const active = activeZone === zone;
            return (
              <Chip
                key={zone}
                label={zoneLabel(zone)}
                onClick={() => setActiveZone(zone)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: active ? 700 : 500,
                  backgroundColor: active ? colors.accentOrange : 'transparent',
                  color: active ? colors.paper : colors.inkMuted,
                  borderColor: active ? colors.accentOrange : colors.border,
                  '&:hover': {
                    backgroundColor: active
                      ? colors.accentOrange
                      : colors.backgroundAlt,
                  },
                }}
              />
            );
          })}
        </Stack>

        {/* Letter-anchored school groups */}
        {isLoading ? (
          <LoadingSpinner minHeight="160px" />
        ) : (
          <>
            <Stack spacing={{ xs: 4, sm: 5 }}>
              {letters.map(letter => (
                <Box key={letter} id={`schools-${letter}`}>
                  <Typography
                    component="h3"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      color: colors.accentOrangeDark,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      mb: 1.5,
                      pb: 0.75,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {letter}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1fr 1fr',
                      },
                      gap: { xs: 0.25, sm: 0.5 },
                    }}
                  >
                    {grouped[letter].map(school => (
                      <a
                        key={school.slug}
                        href={school.routePath}
                        style={{ textDecoration: 'none' }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.9375rem',
                            color: colors.accentCobalt,
                            py: 0.75,
                            px: 1,
                            // Half of radii.sm — a tight highlight on an
                            // inline link, below the smallest card step.
                            borderRadius: '5px',
                            display: 'block',
                            '&:hover': {
                              color: colors.accentOrangeDark,
                              backgroundColor: colors.washAmber,
                            },
                            transition:
                              'color 0.15s ease, background-color 0.15s ease',
                          }}
                        >
                          {school.displayName}
                        </Typography>
                      </a>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>

            {filtered.length === 0 && (
              <Typography
                sx={{ color: colors.inkSubtle, fontSize: '0.9375rem' }}
              >
                {emptyStateMessage}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </LandingSection>
  );
}

export default SchoolDirectory;
